/**
 * F41 (Wave 2 Track D — admin) — organizer pending-approval queue.
 *
 * Route: `/organizers`
 * Permission: `tournaments:approve_organizer` (super_admin + content_moderator).
 *
 * Backed by `GET /admin/organizers` (the real pending-approval queue —
 * paginated `OrganizerProfileWithAccount[]` joined to the owning b2b_account
 * legal/display name). A status chip-filter (All / Pending / Approved /
 * Suspended) drives the list; each row exposes Approve / Suspend actions via the
 * shared `OrganizerDecisionDialog`. Mirrors the admin queue convention (the F40
 * promotions / F39 placement-approvals chip-row precedent).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  Spinner,
  StatusChip,
  type StatusChipStatus,
  Table,
  useToast,
  type TableColumn,
} from '@bravemove/ui-web';
import type {
  OrganizerProfileStatus,
  OrganizerProfileWithAccount,
  OrganizerType,
} from '@bravemove/types';
import { adminTournamentsApi } from '../../api/tournaments';
import { OrganizerDecisionDialog } from '../../components/tournaments/OrganizerDecisionDialog';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';

const ORGANIZER_STATUS_CHIP_MAP: Record<OrganizerProfileStatus, StatusChipStatus> = {
  pending: 'pending',
  approved: 'approved',
  suspended: 'rejected',
};

type StatusFilter = 'all' | OrganizerProfileStatus;
const STATUS_FILTERS: readonly StatusFilter[] = ['all', 'pending', 'approved', 'suspended'];

export function AdminOrganizersPage() {
  const { t } = useI18n();
  const toast = useToast();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [rows, setRows] = useState<OrganizerProfileWithAccount[]>([]);
  // rev-16 — the queue is cursor-paginated server-side (default limit 30);
  // `nextCursor` drives a "Load more" affordance appending pages.
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [target, setTarget] = useState<OrganizerProfileWithAccount | null>(null);
  const [decisionMode, setDecisionMode] = useState<'approve' | 'suspend' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminTournamentsApi.listOrganizers(
        statusFilter === 'all' ? {} : { status: statusFilter },
      );
      setRows([...res.data]);
      setNextCursor(res.nextCursor ?? null);
    } catch (err) {
      setError(translateApiError(err, t));
      setRows([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const res = await adminTournamentsApi.listOrganizers({
        ...(statusFilter === 'all' ? {} : { status: statusFilter }),
        cursor: nextCursor,
      });
      setRows((prev) => [...prev, ...res.data]);
      setNextCursor(res.nextCursor ?? null);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, statusFilter, t]);

  const openDecision = (row: OrganizerProfileWithAccount, mode: 'approve' | 'suspend'): void => {
    setTarget(row);
    setActionError(null);
    setDecisionMode(mode);
  };

  const handleApprove = async (input: {
    reason: string;
    organizerType?: OrganizerType;
  }): Promise<void> => {
    if (!target) return;
    setActionError(null);
    try {
      await adminTournamentsApi.approveOrganizer(target.b2bAccountId, input);
      setDecisionMode(null);
      setTarget(null);
      toast.push({
        tone: 'success',
        title: t('admin:tournament.organizers.approved', { defaultValue: 'Organizer approved.' }),
      });
      await load();
    } catch (err) {
      setActionError(translateApiError(err, t));
    }
  };

  const handleSuspend = async (reason: string): Promise<void> => {
    if (!target) return;
    setActionError(null);
    try {
      await adminTournamentsApi.suspendOrganizer(target.b2bAccountId, reason);
      setDecisionMode(null);
      setTarget(null);
      toast.push({
        tone: 'success',
        title: t('admin:tournament.organizers.suspended', { defaultValue: 'Organizer suspended.' }),
      });
      await load();
    } catch (err) {
      setActionError(translateApiError(err, t));
    }
  };

  const columns = useMemo<readonly TableColumn<OrganizerProfileWithAccount>[]>(
    () => [
      {
        key: 'name',
        header: t('admin:tournament.organizers.name', { defaultValue: 'Organizer' }),
        cell: (row) => (
          <div className="flex flex-col">
            <span className="text-[13px] font-semibold text-ink">{row.accountDisplayName}</span>
            <span className="text-[11.5px] text-ink-3">{row.legalBusinessName}</span>
            <span className="font-mono text-[10.5px] text-ink-3">{row.b2bAccountId}</span>
          </div>
        ),
      },
      {
        key: 'type',
        header: t('admin:tournament.organizers.type', { defaultValue: 'Type' }),
        cell: (row) =>
          t(`tournament:organizer_type.${row.organizerType}`, { defaultValue: row.organizerType }),
      },
      {
        key: 'status',
        header: t('admin:tournament.organizers.status', { defaultValue: 'Status' }),
        cell: (row) => (
          <StatusChip
            status={ORGANIZER_STATUS_CHIP_MAP[row.status]}
            label={t(`tournament:organizer_status.${row.status}`, { defaultValue: row.status })}
            data-testid={`organizer-status-chip-${row.b2bAccountId}`}
          />
        ),
      },
      {
        key: 'actions',
        header: t('admin:tournament.organizers.actions', { defaultValue: 'Actions' }),
        align: 'end',
        cell: (row) => (
          <div className="flex justify-end gap-2">
            {row.status !== 'approved' ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => openDecision(row, 'approve')}
                data-testid={`organizer-approve-cta-${row.b2bAccountId}`}
              >
                {t('admin:tournament.organizers.approve', { defaultValue: 'Approve' })}
              </Button>
            ) : null}
            {row.status !== 'suspended' ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => openDecision(row, 'suspend')}
                data-testid={`organizer-suspend-cta-${row.b2bAccountId}`}
              >
                {t('admin:tournament.organizers.suspend', { defaultValue: 'Suspend' })}
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-6" data-testid="organizers-page">
      <PageHeader
        title={t('admin:tournament.organizers.title', { defaultValue: 'Organizers' })}
        subtitle={t('admin:tournament.organizers.subtitle', {
          defaultValue: 'Approve federation / league organizer partners awaiting review.',
        })}
      />

      <div className="flex flex-wrap gap-2" data-testid="organizer-status-filter">
        {STATUS_FILTERS.map((s) => (
          <Chip
            key={s}
            selected={statusFilter === s}
            onClick={() => setStatusFilter(s)}
            data-testid={`organizer-filter-${s}`}
          >
            {t(`admin:tournament.organizers.filter.${s}`, { defaultValue: s })}
          </Chip>
        ))}
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="organizers-empty">
          <EmptyState
            title={t('admin:tournament.organizers.empty', {
              defaultValue: 'No organizers match this filter.',
            })}
          />
        </div>
      ) : (
        <>
          <Card>
            <Table
              data={rows}
              columns={columns as TableColumn<OrganizerProfileWithAccount>[]}
              rowKey={(row) => row.b2bAccountId}
            />
          </Card>
          {nextCursor !== null ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => void loadMore()}
                loading={loadingMore}
                data-testid="organizers-load-more"
              >
                {t('admin:tournament.organizers.loadMore', { defaultValue: 'Load more' })}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <OrganizerDecisionDialog
        open={decisionMode !== null}
        mode={decisionMode ?? 'approve'}
        onApprove={handleApprove}
        onSuspend={handleSuspend}
        onClose={() => {
          setDecisionMode(null);
          setTarget(null);
        }}
        errorMessage={decisionMode !== null ? actionError : null}
      />
    </div>
  );
}

export default AdminOrganizersPage;
